// recaptchaV3.js
import { useCallback, useEffect, useState } from 'react';

const MAX_RECAPTCHA_TOKEN_ATTEMPTS = 5;
const RECAPTCHA_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

export const isSafeRecaptchaToken = (token) => (
  typeof token === "string" && RECAPTCHA_TOKEN_PATTERN.test(token)
);

const useRecaptchaV3 = (siteKey) => {
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  useEffect(() => {
    const initializeRecaptcha = () => {
      if (window.grecaptcha?.execute) {
        setIsRecaptchaReady(true);
        // Hide the reCAPTCHA badge (branding)
        const recaptchaBadge = document.querySelector('.grecaptcha-badge');
        if (recaptchaBadge) {
          recaptchaBadge.style.visibility = 'hidden';
        }
      } else {
        setTimeout(initializeRecaptcha, 100);
      }
    };
    if (!window.grecaptcha) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      document.head.appendChild(script);
      script.onload = initializeRecaptcha;
    } else {
      initializeRecaptcha();
    }
    return () => {
      const script = document.querySelector(`script[src="https://www.google.com/recaptcha/api.js?render=${siteKey}"]`);
      if (script) script.remove();
    };
  }, [siteKey]);

  const pollForRecaptcha = (resolve, reject) => {
    let timeout;
    const interval = setInterval(() => {
      if (globalThis.grecaptcha?.execute) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve();
      }
    }, 100);
    timeout = setTimeout(() => {
      clearInterval(interval);
      reject(new Error("reCAPTCHA load timeout"));
    }, 10000);
  };

  const executeRecaptcha = useCallback(
    async (action) => {
      const waitForRecaptchaReady = () => (
        new Promise((resolve, reject) => {
          if (isRecaptchaReady && globalThis.grecaptcha?.execute) {
            resolve();
            return;
          }

          pollForRecaptcha(resolve, reject);
        })
      );

      try {
        await waitForRecaptchaReady();

        for (let attempt = 0; attempt < MAX_RECAPTCHA_TOKEN_ATTEMPTS; attempt += 1) {
          const token = await window.grecaptcha.execute(siteKey, { action });

          if (isSafeRecaptchaToken(token)) {
            const hexToken = Array.from(token)
              .map(c => c.codePointAt(0).toString(16).padStart(2, '0'))
              .join('');
            return hexToken;
          }
        }

        return null;
      } catch (error) {
        return null;
      }
    },
    [isRecaptchaReady, siteKey]
  );

  return executeRecaptcha;
};

export default useRecaptchaV3;
